import { create } from "zustand";
import { PersistStorage, persist } from "zustand/middleware";
import { ModeState } from "../utils/app-types";

type Mode = "dark" | "light"; // Define the Mode type

/**
 * Custom localStorage adapter for persisting Zustand state.
 */
const customStorage: PersistStorage<ModeState> = {
  getItem: (key) => {
    const value = localStorage.getItem(key);
    return value !== null ? JSON.parse(value) : null;
  },
  setItem: (key, value) => {
    localStorage.setItem(key, JSON.stringify(value));
  },
  removeItem: (key) => {
    localStorage.removeItem(key);
  },
};

/**
 * Zustand store for managing application mode (light/dark) and notification settings.
 *
 * - Persists the mode and notification state to localStorage.
 * - Provides methods to toggle mode and notification state.
 */
export const useModeStore = create<ModeState>()(
  persist(
    (set, get) => ({
      mode: (localStorage.getItem("joy-mode") as Mode) || "dark", // default mode is dark
      setNotification: (newValue) => set(() => ({notificationEnabled: newValue})),
      setEmailNotification: (newValue) => set(() => ({emailNotificationEnabled: newValue})),
      toggleMode: () =>
        set((state) => {
          const newMode = state.mode === "light" ? "dark" : "light";
          localStorage.setItem("joy-mode", newMode);
          return { mode: newMode }; // needs test
        }),
    }),
    {
      name: "setting-storage",
      // Use the custom storage adapter
      storage: customStorage,
    }
  )
);
